import { Box, Stack, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  H1,
  H3,
  P_REGULAR,
  P_REGULAR_SEMI_BOLD,
  PROVIDER_SITE_CERTIFIER,
  SPAN,
  SUBTITLE,
} from "../../../Constants/CommonConstants";
import { ACCOUNT_SETTING_LABEL } from "../../../Constants/MyProfileConstants";
import {
  MY_PROFILE_ACCOUNT_SETTINGS,
  MY_PROFILE_SITE_ACCESS_LABEL,
} from "../../../Constants/MyProfileSideNav";
import {
  PRIVILEGED_PROVIDER_STAFF_LABEL,
  PRIVILEGED_PROVIDER_STAFF_TOOLTIP,
  PRIVILEGED_PROVIDER_STAFF_TOOLTIP_TEXT,
  PROVIDER_STAFF_LABEL,
  SITE_ACCESS_CERTIFIER_TEXT,
  SITE_ACCESS_STAFF_TEXT,
  SITE_CERTIFIER_LABEL,
} from "../../../Constants/SiteAccessConstants";
import { getAssociatedLocations } from "../../../Features/MyProfileSlice";
import { manageAuthorizationSelector } from "../../../Selectors/AuthorizationSelector";
import {
  StyledAccountSettingsHeader,
  StyledLoginInformationBox,
  StyledTypographyHeader,
} from "../../../Styles/MyProfile/LoginInformation/LoginInformation";
import {
  getAssociatedLocationList,
  getEntitledLocationList,
} from "../../../Services/CommonServices";
import { useEffect } from "react";
import { manageMyProfileSelector } from "../../../Selectors/MyProfileSelector";
import SiteDetailsDisplay from "./SiteDetailsDisplay";
import { PrimaryTooltip, StyledDivider } from "react-common-ui-library";

/**
 * @description Site Access Screen
 * @returns {ReactNode} Displays the associated locations for the user role provider site certifier and staff
 */
export const SiteAccess = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { scopes } = useSelector((state) => manageAuthorizationSelector(state));

  const { userName, associatedLocationsData } = useSelector((state) =>
    manageMyProfileSelector(state)
  );

  const displayText = scopes?.includes(PROVIDER_SITE_CERTIFIER)
    ? SITE_ACCESS_CERTIFIER_TEXT
    : SITE_ACCESS_STAFF_TEXT;

  const labelText = scopes?.includes(PROVIDER_SITE_CERTIFIER)
    ? SITE_CERTIFIER_LABEL
    : PROVIDER_STAFF_LABEL;

  const entitledLocations = getEntitledLocationList(associatedLocationsData);
  const associatedLocations = getAssociatedLocationList(
    associatedLocationsData
  );

  useEffect(() => {
    if (userName) {
      dispatch(
        getAssociatedLocations({
          userName: userName,
          fetchEntitlementInfo: true,
        })
      );
    }
  }, [dispatch, userName]);

  return (
    <StyledLoginInformationBox>
      <Stack gap={3}>
        <Stack gap={2}>
          <StyledAccountSettingsHeader
            id={ACCOUNT_SETTING_LABEL}
            component={H1}
            variant={SUBTITLE}
          >
            {intl.formatMessage({
              id: MY_PROFILE_ACCOUNT_SETTINGS,
            })}
          </StyledAccountSettingsHeader>
          <StyledTypographyHeader
            id={MY_PROFILE_SITE_ACCESS_LABEL}
            component={H1}
            variant={H3}
          >
            {intl.formatMessage({
              id: MY_PROFILE_SITE_ACCESS_LABEL,
            })}
          </StyledTypographyHeader>
        </Stack>
        <Typography variant={P_REGULAR} id={displayText}>
          {intl.formatMessage({ id: displayText })}
        </Typography>
        {entitledLocations.length > 0 && (
          <Stack spacing={3}>
            <Box component={SPAN}>
              <Typography
                variant={P_REGULAR_SEMI_BOLD}
                id={PRIVILEGED_PROVIDER_STAFF_LABEL}
              >
                {intl.formatMessage(
                  {
                    id: PRIVILEGED_PROVIDER_STAFF_LABEL,
                  },
                  { count: entitledLocations.length }
                )}
              </Typography>
              <PrimaryTooltip
                title={intl.formatMessage({
                  id: PRIVILEGED_PROVIDER_STAFF_TOOLTIP_TEXT,
                })}
                baseId={PRIVILEGED_PROVIDER_STAFF_TOOLTIP}
              />
            </Box>
            <SiteDetailsDisplay siteList={entitledLocations} />
          </Stack>
        )}
        {entitledLocations.length > 0 && associatedLocations.length > 0 ? (
          <StyledDivider />
        ) : null}
        {associatedLocations.length > 0 && (
          <Stack spacing={3}>
            <Typography variant={P_REGULAR_SEMI_BOLD} id={labelText}>
              {intl.formatMessage(
                {
                  id: labelText,
                },
                { count: associatedLocations.length }
              )}
            </Typography>
            <SiteDetailsDisplay siteList={associatedLocations} />
          </Stack>
        )}
      </Stack>
    </StyledLoginInformationBox>
  );
};

export default SiteAccess;
